import { redirectToKeycloak } from "common/helpers/redirectToKeycloak";
import { getWhitelabelCustomization } from "common/helpers/utils";

const apiUrl = import.meta.env.VITE_API_URL;

export class HTTPError extends Error {
  code: number;

  type: string;

  serverMessage: string;

  data: string;

  constructor(message: string, code: number) {
    super(message);
    this.name = "HTTPError";
    this.code = code;
    this.type = null;
    this.serverMessage = null;
    this.data = null;
  }
}

type Headers = Record<string, string>;

function headers(token: string, data: unknown = null) {
  const baseHeaders: Headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // Content-Type is not needed for the requests without body
  // Don't set Content-Type for FormData (browser will set it automatically with boundary)
  if (data && !(data instanceof FormData)) {
    baseHeaders["Content-Type"] = "application/json";
  }

  return baseHeaders;
}

function isJsonResponse(response: Response) {
  const contentType = response.headers.get("content-type");
  return contentType && contentType.indexOf("application/json") !== -1;
}

export async function validateStatusCode(response: Response) {
  if (!response.ok) {
    // IF this message is ever changed, make sure to update sentry configuration, since it depends on the message
    // to ignore these errors
    const errorMessage = `Fetch error: Request to ${response.url} failed with status code: ${response.status}`;
    const error = new HTTPError(errorMessage, response.status);

    if (response.status === 401) {
      if (!getWhitelabelCustomization()) {
        // Catch promise rejection to avoid Sentry logging errors
        await redirectToKeycloak(true).catch(() => {});
      }
    }

    if (isJsonResponse(response)) {
      const jsonResponse = await response.json();
      const { error: serverErrorObject } = jsonResponse;
      error.serverMessage = serverErrorObject.message;
      error.type = serverErrorObject.type;
      error.data = serverErrorObject.data;
      throw error;
    } else {
      throw error;
    }
  }
  return response;
}

async function validateContentType(response: Response) {
  if (!isJsonResponse(response)) {
    throw new Error(`Invalid Content Type: ${response.status}`);
  }
  return response;
}

async function parseResponse(response: Response) {
  if (isJsonResponse(response)) {
    return response.json();
  }
  return response.text();
}

// Helper function to prepare request body
function formatBody(data: unknown): string | FormData | null {
  if (data instanceof FormData) {
    return data;
  }

  return data ? JSON.stringify(data) : null;
}

/**
 * Perform a GET HTTP request.
 * @public
 * @param {String} path The request path.
 * @param signal An optional AbortSignal
 * @returns {Promise<Any>} The request response.
 */
export function get(path: string, signal?: AbortSignal) {
  const token = localStorage.getItem("auth");

  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "GET",
    headers: headers(token),
    signal,
  })
    .then(validateStatusCode)
    .then(validateContentType)
    .then(parseResponse);
}

export function post(path: string, data?: unknown, signal?: AbortSignal) {
  const token = localStorage.getItem("auth");
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "POST",
    body: formatBody(data),
    headers: headers(token, data),
    signal,
  })
    .then(validateStatusCode)
    .then(parseResponse);
}

export function put(path: string, data?: unknown) {
  const token = localStorage.getItem("auth");
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "PUT",
    body: formatBody(data),
    headers: headers(token, data),
  })
    .then(validateStatusCode)
    .then(parseResponse);
}

export function patch(path: string, data: unknown, signal?: AbortSignal) {
  const token = localStorage.getItem("auth");
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "PATCH",
    body: formatBody(data),
    headers: headers(token, data),
    signal,
  })
    .then(validateStatusCode)
    .then(parseResponse);
}

export function del(path: string, signal?: AbortSignal) {
  const token = localStorage.getItem("auth");
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "DELETE",
    headers: headers(token),
    signal,
  })
    .then(validateStatusCode)
    .then(parseResponse);
}
